import { datadogRum } from '@datadog/browser-rum';
import Uppy, { Body, Meta, UppyOptions } from '@uppy/core';
// Restrictions isn't re-exported from @uppy/core's own package root (only
// from this internal subpath) -- pre-existing gap from an @uppy major
// version bump, unrelated to TASK-132.
import type { Restrictions } from '@uppy/core/lib/Restricter';
import XHRUpload, { XHRUploadOptions } from '@uppy/xhr-upload';
import { useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import {
    EMPLOYEE_ALLOWED_FILE_TYPES_UPLOAD,
    ALLOWED_FILE_TYPES_UPLOAD,
    MAX_FILE_SIZE_UPLOAD,
    MIN_NUMBER_OF_FILES,
} from '../../constants/files';
import { Document } from '../../interfaces/documents';
import { UppyUploaderProps } from '../../interfaces/uppy';
import { useDocumentFiltered } from '../../queries/documents';

import PDFPreview from './PDFPreview';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import config from 'modules/common/config';
import getCollectiveHeaders, { applyAuth0Authorization } from 'modules/common/getCollectiveHeaders';
import { localViews } from 'modules/Auth/AuthProvider/views';

window.ResizeObserver = window.ResizeObserver || ResizeObserver;

// Only overrides a subset of Restrictions' now-required fields (the rest --
// minFileSize, maxTotalFileSize, requiredMetaFields -- fall back to @uppy's
// own internal defaults via the spread at this function's only call site);
// Partial<Restrictions> reflects that accurately.
const defaultRestrictions = (isEmployee: boolean): Partial<Restrictions> => ({
    maxNumberOfFiles: null,
    minNumberOfFiles: MIN_NUMBER_OF_FILES,
    allowedFileTypes: isEmployee ? EMPLOYEE_ALLOWED_FILE_TYPES_UPLOAD : ALLOWED_FILE_TYPES_UPLOAD,
    maxFileSize: MAX_FILE_SIZE_UPLOAD,
});

const replaceFilename = (filename: string, newFilename: string | undefined) => {
    const parts = filename.split('.');
    const extension = parts.pop();
    return `${newFilename}.${extension}`;
};

const createUppy = (
    documentType: string,
    options: Partial<UppyOptions<Meta, Body>>,
    xhrOptions: Partial<XHRUploadOptions<Meta, Body>> & { baseUrl?: string }
) => {
    const isEmployee = localViews.isEmployee() === true;
    let api = localViews.isEmployee() ? 'documents/ms-member-documents/' : 'documents/member-documents/';

    if (xhrOptions.endpoint) {
        api = xhrOptions.endpoint;
    }
    const endpoint = `${xhrOptions.baseUrl ?? config.djangoURL}${api}`;

    return new Uppy({
        id: documentType,
        meta: { document_type: documentType },
        restrictions: {
            ...defaultRestrictions(isEmployee),
            ...options.restrictions,
        },
        ...options,
    })
        .use(XHRUpload, {
            // getCollectiveHeaders's own return type has named optional
            // properties (no index signature), which Record<string, string>
            // requires structurally even though any object of string values
            // satisfies it at runtime -- cast reflects that, not a behavior
            // change (same gap fixed the same way in Uppy/useUppy.ts).
            headers: () => getCollectiveHeaders() as Record<string, string>,
            ...xhrOptions,
            endpoint,
            withCredentials: true,
            // After the spread, so a caller's own hook runs too rather than replacing this one.
            // Headers above are synchronous; the Auth0 bearer has to be awaited.
            onBeforeRequest: async (xhr, retryCount, files) => {
                await applyAuth0Authorization(xhr);
                await xhrOptions.onBeforeRequest?.(xhr, retryCount, files);
            },
        })
        .use(PDFPreview);
};

const useUppy = ({
    documentType,
    meta = {},
    options = {},
    xhrOptions = {},
    shouldPreloadData,
    filenameReplace,
    memberUUID,
    onUploadSuccess,
    onUploadError,
    onDataLoad,
    onFileAdded,
    onFileRemoved,
    filterByMetaFields = false,
}: UppyUploaderProps) => {
    if (!Object.hasOwn) {
        Object.hasOwn = function hasOwn(elm, key) {
            return Object.prototype.hasOwnProperty.call(elm, key);
        };
    }

    const isEmployee = localViews.isEmployee() === true;

    const { data, isLoading, refetch } = useDocumentFiltered(
        {
            document_type: documentType,
            year: meta?.year ?? null,
            month: meta?.month ?? null,
            quarter: meta?.quarter ?? null,
            ...(filterByMetaFields && meta),
        },
        {
            enabled: shouldPreloadData,
        },
        {
            member_uuid: memberUUID,
        }
    );

    const refMeta = useRef<{
        [key: string]: any;
    }>({});
    const refDocuments = useRef<Document[]>([]);

    const [uppy] = useState(() => createUppy(documentType, options, xhrOptions));

    const handleUpload = () => {
        // Get all the uploaded documents that are fully uploaded
        // getFiles() infers its Meta/Body from the uppy instance itself now
        // (no longer takes type arguments) -- pre-existing API drift.
        const addedDocuments = uppy.getFiles();
        const uploadedDocuments = addedDocuments
            .filter((document) => document?.progress?.uploadComplete === true)
            .map<Document>((document) => ({ ...document?.response?.body, source: 'local' } as Document));

        // Update the documents query data with the uploaded documents
        if (shouldPreloadData) {
            refetch();
        }

        /**
         * When the upload is complete, we want to send a RUM event with
         * the document type and the number of documents uploaded.
         */
        if (addedDocuments.length === uploadedDocuments.length && uploadedDocuments.length > 1) {
            datadogRum.addAction('Multiple documents uploaded', {
                ...refMeta.current,
                documentType,
                type: 'MULTIPLE_DOCUMENTS_UPLOADED',
                numberOfDocuments: uploadedDocuments.length,
            });
        }

        // Call the onUploadSuccess callback with the uploaded documents
        onUploadSuccess?.(uploadedDocuments);
    };

    const handleError = (file: any, error: any, response: any) => {
        sendDatadogRumError('UppyUploader.handleError', error, {
            source: 'UppyUploader.handleError',
            extra: { file, response },
        });
        onUploadError?.(error, response);
    };

    // This function was created because the document/api doesn't allow us to set a default filename of a file
    const handleFilenameReplace = (files: any[]) => {
        files.forEach((file: { id: string; name: any }, key: number) => {
            let newFilename = filenameReplace;

            const fileNumber = refDocuments.current.length + key;

            if (fileNumber > 0) {
                newFilename = `${newFilename}(${fileNumber})`;
            }

            uppy.setFileMeta(file.id, {
                name: replaceFilename(file.name, newFilename),
                meta: {
                    name: replaceFilename(file.name, newFilename),
                },
            });
        });
    };

    const handleFilesAdded = (files: any) => {
        if (filenameReplace) {
            handleFilenameReplace(files);
        }
    };

    const handleFileAdded = (file: { id: string }) => {
        uppy.setFileMeta(file.id, {
            document_type: documentType,
            ...refMeta.current,
        });
        onFileAdded?.(file);
    };

    const handleFileRemoved = (file: File | undefined) => {
        onFileRemoved?.(file);
    };

    useEffect(() => {
        refDocuments.current = data ?? [];
        onDataLoad?.(data ?? []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        // We create a new metadata object with the document type and any other metadata fields passed in as props.
        // If we have a member UUID, we add it to the metadata object as well.
        const newMeta = memberUUID
            ? { document_type: documentType, ...meta, member: memberUUID }
            : { document_type: documentType, ...meta };

        // We store the current metadata in a ref so we can access it inside an event handler.
        refMeta.current = meta;
        // We set the Uppy metadata to the new metadata object we created.
        uppy.setMeta(newMeta);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentType, meta, memberUUID]);

    useEffect(() => {
        uppy.on('upload-success', handleUpload);
        uppy.on('upload-error', handleError);
        uppy.on('file-added', handleFileAdded);
        uppy.on('files-added', handleFilesAdded);
        // 'file-removed' actually calls its listener with an UppyFile, not a
        // raw File -- handleFileRemoved's own File|undefined param type (and
        // the onFileRemoved prop it forwards to, in interfaces/uppy.ts) has
        // been wrong since some prior @uppy version bump, pre-existing and
        // unrelated to TASK-132. Left as-is rather than widened into a
        // third file with unknown consumers, since UppyFile and File only
        // happen to overlap on the fields callers have been duck-typing.
        // @ts-expect-error -- see comment above
        uppy.on('file-removed', handleFileRemoved);

        return () => {
            uppy.off('upload-success', handleUpload);
            uppy.off('upload-error', handleError);
            uppy.off('file-added', handleFileAdded);
            uppy.off('files-added', handleFilesAdded);
            // @ts-expect-error -- see comment above
            uppy.off('file-removed', handleFileRemoved);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uppy]);

    return {
        uppy,
        documents: data ?? [],
        isLoading: shouldPreloadData ? isLoading : false,
        restrictions: { ...defaultRestrictions(isEmployee), ...options.restrictions },
    };
};

export default useUppy;
